import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePageTitle } from '../hooks/usePageTitle'
import { dealsApi, type Deal } from '../api/deals'
import { customStatusesApi, type CustomStatus } from '../api/settings'
import { organizationsApi, type Organization } from '../api/contacts'
import { KanbanBoard } from '../components/KanbanBoard'
import { Pagination } from '../components/ui/Pagination'
import { usePersistedPageSize } from '../hooks/usePersistedPageSize'
import { Modal } from '../components/ui/Modal'
import { PageSpinner } from '../components/ui/Spinner'
import { EmptyState } from '../components/ui/EmptyState'
import { Avatar } from '../components/ui/Avatar'
import { SourceBadge } from '../components/ui/SourceBadge'
import { SLATimer } from '../components/SLATimer'
import { formatDate, formatCurrency, fullName } from '../lib/utils'
import { DropdownSelect } from '../components/ui/DropdownSelect'
import { Plus, List, Columns, BarChart2, Download, Search, RotateCcw } from 'lucide-react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/auth'

type ViewMode = 'list' | 'kanban'

export function DealsListPage() {
  usePageTitle('Deals')
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const canSeeDeleted = user?.role === 'administrator' || user?.role === 'company_owner'
  const isSalesUser = user?.role === 'sales_user'
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = usePersistedPageSize('deals', companyId)
  const [showCreate, setShowCreate] = useState(false)
  const [search, setSearch] = useState('')
  // Toggle to surface soft-deleted deals (admin/owner only). Mirrors the
  // assignment-rules Show inactive pattern. Default off — most users
  // never need to see deleted rows.
  const [showDeleted, setShowDeleted] = useState(false)
  const [orgFilter, setOrgFilter] = useState('')

  const { data: orgs } = useQuery({
    queryKey: ['organizations', companyId],
    queryFn: () => organizationsApi.list(companyId!, { limit: 500 }).then(r => r.data.data),
    enabled: !!companyId,
  })
  const orgMap = new Map((orgs ?? []).map((o: Organization) => [o.id, o]))

  const { data, isLoading } = useQuery({
    queryKey: ['deals', companyId, page, limit, search, showDeleted, orgFilter],
    queryFn: () => dealsApi.list(companyId!, {
      page, limit, search: search || undefined,
      includeDeleted: showDeleted ? true : undefined,
      filters: orgFilter ? JSON.stringify([{ field: 'organizationId', operator: 'eq', value: orgFilter }]) : undefined,
    }).then(r => r.data),
    enabled: !!companyId,
  })

  const restoreMutation = useMutation({
    mutationFn: (dealId: string) => dealsApi.restore(companyId!, dealId),
    onSuccess: () => {
      toast.success('Deal restored')
      qc.invalidateQueries({ queryKey: ['deals', companyId] })
      qc.invalidateQueries({ queryKey: ['deals-all', companyId] })
      qc.invalidateQueries({ queryKey: ['dashboard-overview', companyId] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to restore deal')
    },
  })

  const { data: allDeals } = useQuery({
    queryKey: ['deals-all', companyId],
    queryFn: () => dealsApi.list(companyId!, { limit: 500 }).then(r => r.data),
    enabled: !!companyId && viewMode === 'kanban',
  })

  const { data: statuses } = useQuery({
    queryKey: ['custom-statuses', companyId],
    queryFn: () => customStatusesApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  const stageChangeMutation = useMutation({
    mutationFn: ({ dealId, status }: { dealId: string; status: string }) =>
      dealsApi.updateStage(companyId!, dealId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', companyId] })
      qc.invalidateQueries({ queryKey: ['deals-all', companyId] })
    },
  })

  const exportCsv = async () => {
    const res = await dealsApi.export(companyId!)
    const url = URL.createObjectURL(new Blob([res.data]))
    const a = document.createElement('a'); a.href = url; a.download = 'deals.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const dealStatuses = statuses?.filter(s => s.applyTo === 'deal') ?? []

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <h1 className="page-title">Deals</h1>
          {data && <span className="badge badge-gray">{data.meta.total}</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-gray-200 overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 ${viewMode === 'list' ? 'bg-primary-50 text-primary-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 border-l border-gray-200 ${viewMode === 'kanban' ? 'bg-primary-50 text-primary-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              <Columns className="w-4 h-4" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search deals..."
              className="input pl-8 h-8 text-sm w-48"
            />
          </div>
          {canSeeDeleted && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={e => { setShowDeleted(e.target.checked); setPage(1) }}
              />
              Show deleted
            </label>
          )}
          {orgs && orgs.length > 0 && (
            <DropdownSelect
              value={orgFilter}
              onChange={v => { setOrgFilter(v); setPage(1) }}
              options={(orgs ?? []).map((o: Organization) => ({ value: o.id, label: o.name }))}
              placeholder="All Organizations"
              onClear={() => { setOrgFilter(''); setPage(1) }}
              clearLabel="All Organizations"
            />
          )}
          {!isSalesUser && (
            <button onClick={exportCsv} className="btn btn-secondary btn-sm">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          )}
          <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
            <Plus className="w-3.5 h-3.5" /> New Deal
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? <PageSpinner /> : viewMode === 'kanban' ? (
          <div className="p-4 h-full">
            <KanbanBoard
              deals={allDeals?.data ?? []}
              statuses={dealStatuses}
              onStageChange={(dealId, status) => stageChangeMutation.mutate({ dealId, status })}
            />
          </div>
        ) : (
          <div className="bg-white">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Stage</th>
                  <th>Source</th>
                  <th>Value</th>
                  <th>Assigned</th>
                  <th>Close Date</th>
                  <th>SLA</th>
                  <th>Created</th>
                  <th>Campaign</th>
                  {showDeleted && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {data?.data.length === 0 && (
                  <tr>
                    <td colSpan={showDeleted ? 10 : 9} className="py-16 text-center">
                      <EmptyState
                        icon={<BarChart2 className="w-10 h-10" />}
                        title={showDeleted ? 'No deals (active or deleted)' : 'No deals yet'}
                        action={<button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">New Deal</button>}
                      />
                    </td>
                  </tr>
                )}
                {data?.data.map((deal: Deal) => {
                  const assigneeName = deal.assignedUser
                    ? fullName(deal.assignedUser.firstName, deal.assignedUser.lastName)
                    : null
                  const stageColor = dealStatuses.find(s => s.label === deal.status)?.color
                  const isDeleted = !!deal.deletedAt
                  return (
                    <tr
                      key={deal.id}
                      // Soft-deleted rows: clicking does nothing (the detail
                      // page would 404 since findOne filters soft-deleted).
                      // The user must Restore first to open the deal.
                      onClick={() => { if (!isDeleted) navigate(`/company/${companyId}/deals/${deal.id}`) }}
                      className={isDeleted ? 'opacity-60 bg-gray-50 cursor-default' : 'cursor-pointer'}
                    >
                      <td className={`font-medium ${isDeleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{deal.title}</td>
                      <td>
                        <span
                          className="badge text-white"
                          style={{ backgroundColor: stageColor ?? '#6b7280' }}
                        >
                          {deal.status}
                        </span>
                      </td>
                      <td><SourceBadge source={deal.source} /></td>
                      <td className={isDeleted ? 'text-gray-400 font-medium' : 'text-green-700 font-medium'}>
                        {formatCurrency(deal.value, deal.currency)}
                      </td>
                      <td>
                        {assigneeName ? (
                          <div className="flex items-center gap-1.5">
                            <Avatar name={assigneeName} size="sm" />
                            <span className="text-xs">{assigneeName}</span>
                          </div>
                        ) : <span className="text-gray-400 text-xs">Unassigned</span>}
                      </td>
                      <td className="text-gray-500 text-xs">{formatDate(deal.expectedCloseDate)}</td>
                      <td><SLATimer deadline={deal.slaDeadline} breached={deal.slaBreahed} /></td>
                      <td className="text-gray-500 text-xs">{formatDate(deal.createdAt)}</td>
                      <td>
                        {deal.organizationId && orgMap.has(deal.organizationId) && (() => {
                          const org = orgMap.get(deal.organizationId!)!
                          return (
                            <span className="badge text-white text-xs" style={{ backgroundColor: org.color ?? '#7c3aed' }}>
                              {org.name}
                            </span>
                          )
                        })()}
                      </td>
                      {showDeleted && (
                        <td onClick={e => e.stopPropagation()}>
                          {isDeleted && (
                            <div className="flex justify-end">
                              <button
                                onClick={() => restoreMutation.mutate(deal.id)}
                                disabled={restoreMutation.isPending}
                                className="btn btn-ghost btn-sm text-xs text-green-700 hover:bg-green-50"
                                title="Restore deal"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <span className="ml-1">Restore</span>
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewMode === 'list' && data && data.meta.total > 0 && (
        <Pagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={limit}
          onPageChange={setPage}
          onPageSizeChange={size => { setLimit(size); setPage(1) }}
        />
      )}

      <CreateDealModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        companyId={companyId!}
        statuses={dealStatuses}
        onSuccess={() => {
          setShowCreate(false)
          qc.invalidateQueries({ queryKey: ['deals', companyId] })
        }}
      />
    </div>
  )
}

interface CreateDealFormData {
  title: string; status: string; value?: number; currency?: string; expectedCloseDate?: string
}

function CreateDealModal({ open, onClose, companyId, statuses, onSuccess }: {
  open: boolean; onClose: () => void; companyId: string
  statuses: CustomStatus[]; onSuccess: () => void
}) {
  const { register, handleSubmit, reset, watch, setValue } = useForm<CreateDealFormData>({
    defaultValues: { currency: 'EUR', status: statuses[0]?.label ?? '' },
  })
  const mutation = useMutation({
    mutationFn: (dto: CreateDealFormData) => dealsApi.create(companyId, dto),
    onSuccess: () => { reset(); toast.success('Deal created'); onSuccess() },
  })
  return (
    <Modal open={open} onClose={onClose} title="New Deal" size="md">
      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
        <div><label className="label">Title *</label><input {...register('title', { required: true })} className="input" /></div>
        <div>
          <label className="label">Stage</label>
          <DropdownSelect
            value={watch('status') ?? ''}
            onChange={v => setValue('status', v)}
            options={statuses.map(s => ({ value: s.label, label: s.label }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Value</label><input {...register('value', { valueAsNumber: true })} type="number" min="0" step="0.01" className="input" /></div>
          <div><label className="label">Currency</label><input {...register('currency')} className="input" defaultValue="EUR" /></div>
        </div>
        <div><label className="label">Expected Close Date</label><input {...register('expectedCloseDate')} type="date" className="input" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Cancel</button>
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Create Deal</button>
        </div>
      </form>
    </Modal>
  )
}
